/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from "@solana/kit";
import {
  getCredentialKindDecoder,
  getCredentialKindEncoder,
  type CredentialKind,
  type CredentialKindArgs,
} from "../types";

export const CREDENTIAL_TYPE_DISCRIMINATOR = new Uint8Array([
  132, 171, 20, 181, 39, 219, 217, 112,
]);

export function getCredentialTypeDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CREDENTIAL_TYPE_DISCRIMINATOR,
  );
}

export type CredentialType = {
  discriminator: ReadonlyUint8Array;
  /** Authority that can manage this type (governance/multisig) */
  authority: Address;
  /** Unique name for this credential type */
  name: string;
  /** The kind of credential (built-in or custom) */
  kind: CredentialKind;
  /** URI to the JSON-LD schema (IPFS or HTTPS) */
  schemaUri: string;
  /** Human-readable description */
  description: string;
  /** Whether this type is active (can issue new credentials) */
  isActive: boolean;
  /** Total credentials issued of this type */
  totalIssued: bigint;
  /** Creation timestamp */
  createdAt: bigint;
  /** PDA bump */
  bump: number;
};

export type CredentialTypeArgs = {
  /** Authority that can manage this type (governance/multisig) */
  authority: Address;
  /** Unique name for this credential type */
  name: string;
  /** The kind of credential (built-in or custom) */
  kind: CredentialKindArgs;
  /** URI to the JSON-LD schema (IPFS or HTTPS) */
  schemaUri: string;
  /** Human-readable description */
  description: string;
  /** Whether this type is active (can issue new credentials) */
  isActive: boolean;
  /** Total credentials issued of this type */
  totalIssued: number | bigint;
  /** Creation timestamp */
  createdAt: number | bigint;
  /** PDA bump */
  bump: number;
};

/** Gets the encoder for {@link CredentialTypeArgs} account data. */
export function getCredentialTypeEncoder(): Encoder<CredentialTypeArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["authority", getAddressEncoder()],
      ["name", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["kind", getCredentialKindEncoder()],
      ["schemaUri", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["description", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["isActive", getBooleanEncoder()],
      ["totalIssued", getU64Encoder()],
      ["createdAt", getI64Encoder()],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({ ...value, discriminator: CREDENTIAL_TYPE_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link CredentialType} account data. */
export function getCredentialTypeDecoder(): Decoder<CredentialType> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["authority", getAddressDecoder()],
    ["name", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["kind", getCredentialKindDecoder()],
    ["schemaUri", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["description", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["isActive", getBooleanDecoder()],
    ["totalIssued", getU64Decoder()],
    ["createdAt", getI64Decoder()],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link CredentialType} account data. */
export function getCredentialTypeCodec(): Codec<
  CredentialTypeArgs,
  CredentialType
> {
  return combineCodec(getCredentialTypeEncoder(), getCredentialTypeDecoder());
}

export function decodeCredentialType<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<CredentialType, TAddress>;
export function decodeCredentialType<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<CredentialType, TAddress>;
export function decodeCredentialType<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<CredentialType, TAddress> | MaybeAccount<CredentialType, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getCredentialTypeDecoder(),
  );
}

export async function fetchCredentialType<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<CredentialType, TAddress>> {
  const maybeAccount = await fetchMaybeCredentialType(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeCredentialType<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<CredentialType, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeCredentialType(maybeAccount);
}

export async function fetchAllCredentialType(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<CredentialType>[]> {
  const maybeAccounts = await fetchAllMaybeCredentialType(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeCredentialType(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<CredentialType>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeCredentialType(maybeAccount),
  );
}
